// GoLiveStep.tsx — onboarding step 5, the final step (spec D6, handoff §6 step "Go live").
// Snippet + copy button render through the shared `EmbedSnippet` component's `'overlay'`
// variant (fix round 1 — this used to carry its own third copy of the snippet string and
// clipboard logic, the same duplication `EmbedSnippet.tsx`'s extraction out of `BotSettings.tsx`
// was meant to close; see that file's `variant` doc comment). The "we'll email you on first
// answer" line was held back at M5 (the notification didn't exist yet — M5 task-5's explicit
// override) and lands here now that `first_answer_notify` is wired (M6 Task 5, spec D2) —
// wording is verbatim from the design handoff (`docs/design/design_handoff_embent_design_system/
// New Screens.dc.html`, go-live section), not the M6 plan's own paraphrase.
import { Button } from '@repo/ui/components/button';
import type { BotDto } from '@repo/shared-types';
import { EmbedSnippet } from '../EmbedSnippet';
import { landingUrl } from '../../lib/landing-url';
import { useT } from '../../locales/context';

export function GoLiveStep({
  bot,
  onGoToDashboard,
  onViewSources,
}: {
  bot: BotDto;
  onGoToDashboard: () => void;
  onViewSources: () => void;
}) {
  const t = useT();
  return (
    <div className="flex flex-col gap-5 rounded-[14px] border bg-card p-8 shadow-el-1">
      <div className="flex flex-col gap-2">
        <div
          className="grid h-11 w-11 place-items-center rounded-full text-xl font-bold"
          style={{
            color: 'var(--st-ready)',
            backgroundColor: 'color-mix(in srgb, var(--st-ready) var(--st-wash), transparent)',
          }}
          aria-hidden="true"
        >
          ✓
        </div>
        <h1 className="font-display text-[26px] font-bold tracking-[-0.01em]">
          {t.onboarding.goLiveHeading}
        </h1>
        <p className="text-sm text-pretty text-muted-foreground">
          {t.onboarding.goLivePasteBeforePrefix}{' '}
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[12.5px]">&lt;/body&gt;</code>{' '}
          {t.onboarding.goLivePasteBeforeSuffix}
        </p>
      </div>

      <EmbedSnippet publicId={bot.publicId} variant="overlay" />

      <a
        href={`${landingUrl()}/docs/install`}
        target="_blank"
        rel="noreferrer"
        className="w-fit text-xs font-medium text-primary hover:underline"
      >
        {t.onboarding.goLiveInstallGuidesLink}
      </a>

      <p className="text-xs text-muted-foreground">{t.onboarding.goLiveFirstAnswerNotice}</p>

      <div className="flex flex-col gap-2">
        <Button type="button" className="h-[42px] w-full" onClick={onGoToDashboard}>
          {t.onboarding.goLiveDashboardCta}
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={onViewSources}>
          {t.onboarding.goLiveViewSourcesCta}
        </Button>
      </div>
    </div>
  );
}
